/**
 * usePasswordResetFlow — the ONE password-reset state machine: email → code → new password → done.
 *
 * Extracted from `<PasswordResetModal>` and rebuilt by UNREACHABLE-MODULE-BURNDOWN-001 L16, which
 * deleted palm-web's never-mounted app-local reset tree after measuring what it had that this flow
 * lacked. Everything worth having came across; the simulations went:
 *
 * RETIRED — each made the flow report something that had not happened:
 *   - the `simple` one-shot flow, which with no `onSubmit` waited 500 ms and reported a reset sent
 *     (reached for real by planner-web's platform-admin login, for platform staff) and reported
 *     success on every failure;
 *   - the no-callback fallbacks: `onInitiate` absent → "sent", `onVerify` absent → a literal
 *     `'test-token'`, `onComplete` absent → "changed". All four callbacks are REQUIRED now;
 *   - the server `redirectUrl` hop (and its open-redirect guard and delay): no reset route in the
 *     fleet has ever returned one, and no host ever passed the options that configured it.
 *     `onSuccess` stays — pos-web's `ManagerPasswordResetPage` returns to sign-in through it — and
 *     is called with nothing, because there was never anything to pass.
 *
 * BUILT — what palm's copy had and this lacked, each judged best practice before it was ported:
 *   - a RESEND COOLDOWN with a visible countdown. Every reset router refuses a resend inside 60 s;
 *     the button used to stay live and invite a refusal. A refusal's `retryAfter` RESYNCHRONISES
 *     the countdown — the server is the authority on its own window (`useResendCountdown`).
 *   - BACK from the code step, for a mistyped address — and taken automatically when the code is
 *     dead: after too many wrong codes the router DELETES it, so "Resend" there silently sends
 *     nothing, and a reset token that expired can only be replaced by a new code.
 *   - translated failures, classified by what the employee can do next (see `classifyResetError`).
 *   - show / hide on the new-password fields, beside the live requirement checklist.
 *
 * The PASSWORD is judged by the ONE ecosystem policy, `@puffin-core/password-policy` (NIST SP
 * 800-63B-4, L-R7CLOSE rows 39 / 42) — the last step, its gate and its policy refusal live in
 * `usePasswordResetCompletion`, which this flow composes.
 *
 * NOT ported, on the record: palm's client-side "N attempts remaining" counter (it restarted on a
 * reload and duplicated a number only the server holds), its composition-count "strength" meter
 * (it called `Password1!` strong; the checklist carries the same information honestly), and
 * auto-submit on the sixth digit (a change of context on input — WCAG 3.2.2 — that also spends one
 * of five attempts on a typo).
 *
 * @module @puffin-core/login-shell/hooks/usePasswordResetFlow
 */

import { useCallback, useReducer } from 'react';
// The NARROW subpath, never the barrel: the countdown module imports nothing but React, while the
// barrel drags in the whole MFA surface — and every suite that doubles `@puffin-core/mfa-ui` to
// stub `<MfaVerification>` would otherwise erase the countdown with it.
import { useResendCountdown } from '@puffin-core/mfa-ui/resend-countdown';
// The NARROW subpath: the ONE rule a refused reset is read by, shared with palm-native's reset screen.
import { classifyResetRefusal, RESET_REFUSALS } from '@puffin-core/auth-shell/reset-refusal';

import { usePasswordResetCompletion } from './usePasswordResetCompletion.js';

export const PasswordResetStep = Object.freeze({
  EMAIL: 'email',
  CODE: 'code',
  NEW_PASSWORD: 'newPassword',
  SUCCESS: 'success',
});

/** The three steps a user moves through (SUCCESS is where they land, not a step they take). */
export const RESET_STEP_ORDER = Object.freeze([
  PasswordResetStep.EMAIL,
  PasswordResetStep.CODE,
  PasswordResetStep.NEW_PASSWORD,
]);

/** Every reset router's default resend window (`createPasswordResetRouter`'s `resendCooldownMs`). */
export const DEFAULT_RESEND_COOLDOWN_SECONDS = 60;

/**
 * @param {string} initialEmail
 * @returns {Object} a fresh flow state on the EMAIL step
 */
function createInitialState(initialEmail) {
  return {
    step: PasswordResetStep.EMAIL,
    email: initialEmail,
    // The company the code was SENT for — resolved once, when the code is requested, and reused by the verify,
    // the resend and the new-password context, so every step of one reset is about one company (U1).
    subdomain: '',
    code: '',
    newPassword: '',
    confirmPassword: '',
    resetToken: '',
    showPassword: false,
    isLoading: false,
    error: null,
  };
}

/**
 * The flow's reducer. Exported for direct unit coverage of the `default` arm, which no dispatch
 * site in this module reaches.
 *
 * @param {Object} state
 * @param {{type: string}} action
 * @returns {Object} the next state
 */
export function resetReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value, error: null };
    case 'TOGGLE_SHOW_PASSWORD':
      return { ...state, showPassword: !state.showPassword };
    case 'RESET':
      return createInitialState(action.initialEmail);
    case 'SUBMIT_START':
      return { ...state, isLoading: true, error: null };
    case 'SUBMIT_ERROR':
      return { ...state, isLoading: false, error: action.error };
    case 'EMAIL_SENT':
      return {
        ...state,
        isLoading: false,
        step: PasswordResetStep.CODE,
        code: '',
        subdomain: action.subdomain ?? state.subdomain,
      };
    case 'BACK_TO_EMAIL':
      return {
        ...state,
        isLoading: false,
        step: PasswordResetStep.EMAIL,
        code: '',
        resetToken: '',
        newPassword: '',
        confirmPassword: '',
        error: action.error ?? null,
      };
    case 'CODE_VERIFIED':
      return {
        ...state,
        isLoading: false,
        step: PasswordResetStep.NEW_PASSWORD,
        resetToken: action.resetToken,
      };
    case 'PASSWORD_CHANGED':
      return { ...state, isLoading: false, step: PasswordResetStep.SUCCESS };
    default:
      return state;
  }
}

/**
 * This modal's words for each refusal `classifyResetRefusal` names. A literal map, so every leaf is
 * a key catalog reachability can read.
 */
const RESET_REFUSAL_KEYS = Object.freeze({
  [RESET_REFUSALS.NETWORK]: 'modal.passwordReset.error.network',
  [RESET_REFUSALS.CAPTCHA]: 'modal.passwordReset.error.captcha',
  [RESET_REFUSALS.NOT_RECORDED]: 'modal.passwordReset.error.notRecorded',
  [RESET_REFUSALS.CODE_LOCKED]: 'modal.passwordReset.error.codeLocked',
  [RESET_REFUSALS.TOO_SOON]: 'modal.passwordReset.error.tooSoon',
  [RESET_REFUSALS.RATE_LIMITED]: 'modal.passwordReset.error.rateLimited',
  [RESET_REFUSALS.CODE_INVALID]: 'modal.passwordReset.error.codeInvalid',
  [RESET_REFUSALS.SESSION_EXPIRED]: 'modal.passwordReset.error.sessionExpired',
  [RESET_REFUSALS.PASSWORD_REJECTED]: 'modal.passwordReset.error.passwordRejected',
  [RESET_REFUSALS.INVALID_FIELD]: 'modal.passwordReset.error.invalidField.other',
  [RESET_REFUSALS.FAILED]: 'modal.passwordReset.error.failed',
});

/**
 * PLANNER-PALM-UAT-2026-11-03-001 U1: an `invalidField` refusal is worded by the field the server refused — the email,
 * the company (the empty subdomain every tenant page used to send), the code, the new password. A field this map does
 * not name reads `invalidField.other`. A literal map, so catalog reachability reads every leaf.
 */
const INVALID_FIELD_KEYS = Object.freeze({
  email: 'modal.passwordReset.error.invalidField.email',
  subdomain: 'modal.passwordReset.error.invalidField.subdomain',
  code: 'modal.passwordReset.error.codeFormat',
  newPassword: 'modal.passwordReset.error.requirementsUnmet',
});

/**
 * Translate a rejected reset request into what the employee can DO next. What a refusal MEANS is
 * decided once, by auth-shell's `classifyResetRefusal` (PALM-RESET-FLOW-001: palm-native's reset
 * screen reads the same rule); this modal only words it from its own catalog.
 *
 * @param {unknown} err - the rejection (`usePasswordResetClient`'s vocabulary error)
 * @param {'initiate'|'verify'|'complete'|'resend'} stage
 * @returns {{key: string, params?: Object, backToEmail?: boolean, retryAfter?: number}}
 */
export function classifyResetError(err, stage) {
  const { refusal, backToEmail, retryAfter, field } = classifyResetRefusal(err, stage);
  const key =
    (refusal === RESET_REFUSALS.INVALID_FIELD && Object.hasOwn(INVALID_FIELD_KEYS, field)
      ? INVALID_FIELD_KEYS[field]
      : null) ?? RESET_REFUSAL_KEYS[refusal];
  if (retryAfter) return { key, params: { seconds: retryAfter }, retryAfter };
  return backToEmail ? { key, backToEmail } : { key };
}

/**
 * @param {Object} options
 * @param {string} options.initialEmail
 * @param {() => (string|Promise<string>)} options.resolveSubdomain - the company the reset is for, asked when the
 *   code is requested (the sign-in form's `resolveTenant`: the typed subdomain, else the auth context's binding)
 * @param {(email: string, subdomain: string) => Promise<any>} options.onInitiate
 * @param {(email: string, subdomain: string, code: string) => Promise<{resetToken: string}>} options.onVerify
 * @param {(resetToken: string, newPassword: string) => Promise<any>} options.onComplete
 * @param {(email: string, subdomain: string) => Promise<any>} options.onResend
 * @param {() => void} [options.onSuccess] - called once the new password is saved
 * @param {number} options.resendCooldownSeconds
 * @returns {Object} the flow state, the countdown, and every action the steps take
 */
export function usePasswordResetFlow({
  initialEmail,
  resolveSubdomain,
  onInitiate,
  onVerify,
  onComplete,
  onResend,
  onSuccess,
  resendCooldownSeconds,
}) {
  const [state, dispatch] = useReducer(resetReducer, initialEmail, createInitialState);
  const { secondsRemaining, start: startCountdown } = useResendCountdown(resendCooldownSeconds);

  /**
   * Record a refusal; resynchronise the countdown to the server's window when it named one. `text`
   * is a sentence already translated for this refusal (a password-policy refusal names what failed),
   * which the alert shows in place of the class sentence.
   */
  const fail = useCallback(
    (err, stage, text) => {
      const { key, params, backToEmail, retryAfter } = classifyResetError(err, stage);
      const error = text ? { key, params, text } : { key, params };
      if (retryAfter) startCountdown(retryAfter);
      dispatch(backToEmail ? { type: 'BACK_TO_EMAIL', error } : { type: 'SUBMIT_ERROR', error });
    },
    [startCountdown],
  );

  const handleOpen = useCallback(() => {
    // The countdown deliberately SURVIVES a close and reopen: the server's window does too.
    dispatch({ type: 'RESET', initialEmail });
  }, [initialEmail]);

  const setField = useCallback((field, value) => dispatch({ type: 'SET_FIELD', field, value }), []);
  const toggleShowPassword = useCallback(() => dispatch({ type: 'TOGGLE_SHOW_PASSWORD' }), []);
  const handleBackToEmail = useCallback(() => dispatch({ type: 'BACK_TO_EMAIL' }), []);

  const handleInitiate = useCallback(
    async (e) => {
      e.preventDefault();
      if (!state.email.trim() || state.isLoading) return;
      dispatch({ type: 'SUBMIT_START' });
      try {
        const subdomain = await resolveSubdomain();
        await onInitiate(state.email.trim(), subdomain);
        dispatch({ type: 'EMAIL_SENT', subdomain });
        startCountdown();
      } catch (err) {
        fail(err, 'initiate');
      }
    },
    [state.email, state.isLoading, onInitiate, resolveSubdomain, startCountdown, fail],
  );

  const handleVerifyCode = useCallback(
    async (e) => {
      e.preventDefault();
      if (state.isLoading) return;
      if (!/^\d{6}$/.test(state.code)) {
        dispatch({ type: 'SUBMIT_ERROR', error: { key: 'modal.passwordReset.error.codeFormat' } });
        return;
      }
      dispatch({ type: 'SUBMIT_START' });
      try {
        // The client hands back the envelope's payload, so the token is top-level.
        const result = await onVerify(state.email.trim(), state.subdomain, state.code);
        if (!result?.resetToken) {
          dispatch({ type: 'SUBMIT_ERROR', error: { key: 'modal.passwordReset.error.failed' } });
          return;
        }
        dispatch({ type: 'CODE_VERIFIED', resetToken: result.resetToken });
      } catch (err) {
        fail(err, 'verify');
      }
    },
    [state.code, state.email, state.subdomain, state.isLoading, onVerify, fail],
  );

  const { passwordContext, handleCompleteReset } = usePasswordResetCompletion({
    state,
    dispatch,
    onComplete,
    onSuccess,
    fail,
  });

  const handleResend = useCallback(async () => {
    if (state.isLoading || secondsRemaining > 0) return;
    dispatch({ type: 'SUBMIT_START' });
    try {
      await onResend(state.email.trim(), state.subdomain);
      dispatch({ type: 'EMAIL_SENT' });
      startCountdown();
    } catch (err) {
      fail(err, 'resend');
    }
  }, [
    state.isLoading,
    secondsRemaining,
    state.email,
    state.subdomain,
    onResend,
    startCountdown,
    fail,
  ]);

  return {
    state,
    passwordContext,
    secondsUntilResend: secondsRemaining,
    setField,
    toggleShowPassword,
    handleOpen,
    handleInitiate,
    handleVerifyCode,
    handleCompleteReset,
    handleResend,
    handleBackToEmail,
  };
}

export default usePasswordResetFlow;
